import { useState, useEffect } from 'react';
import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';

const CF_URL = 'https://itops.octopushq.com/status.json';

// ── Azure SSO config ──────────────────────────────────────────────────────────
const TENANT_ID  = '18eb006b-c3c8-4a72-93cd-fe4b293f82ee';
const CLIENT_ID  = '306a80ed-ecba-4ee4-86b6-283f43b32c42';
const GROUP_ID   = 'f860cf30-40b1-4f32-8273-19dcfe5a1ace';
const REDIRECT   = 'https://itops.octopushq.com/auth';

const msalInstance = new PublicClientApplication({
  auth: {
    clientId: CLIENT_ID,
    authority: `https://login.microsoftonline.com/${TENANT_ID}`,
    redirectUri: REDIRECT,
  },
  cache: { cacheLocation: 'sessionStorage' },
});

// ── Service groups ────────────────────────────────────────────────────────────
const GROUPS = {
  'Atlassian':       ['jiraSoftware', 'jiraServiceMgmt', 'confluence', 'bitbucket', 'rovo', 'atlassianDev'],
  'Other Tools':     ['github', 'slack', 'salesforce', 'zoom', 'calendly', 'whimsical'],
  'Microsoft Azure': ['entraId', 'intune', 'azureDevOps', 'appService', 'virtualMachines', 'azureStorage', 'sqlDatabase', 'azureKubernetes', 'azureOverall'],
  'Google':          ['gmail', 'googleDrive', 'googleMeet', 'googleDocs', 'googleCalendar', 'gemini'],
  'AI Tools':        ['openai', 'claude', 'cursor'],
};

const GROUP_ICONS = {
  'Atlassian': '🔵', 'Other Tools': '⚙️', 'Microsoft Azure': '🔷', 'Google': '🌐', 'AI Tools': '🤖',
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function indicatorStyle(ind) {
  if (ind === 'none')                          return { dot: '#639922', label: 'Operational', badge: '#EAF3DE', badgeText: '#3B6D11' };
  if (ind === 'minor' || ind === 'degraded')   return { dot: '#BA7517', label: 'Degraded',    badge: '#FAEEDA', badgeText: '#854F0B' };
  if (ind === 'major' || ind === 'critical')   return { dot: '#E24B4A', label: 'Outage',      badge: '#FCEBEB', badgeText: '#A32D2D' };
  return { dot: '#888780', label: 'Unknown', badge: '#F1EFE8', badgeText: '#5F5E5A' };
}

function formatTime(iso) {
  return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', timeZoneName: 'short' });
}

// ── Service Card ──────────────────────────────────────────────────────────────
function ServiceCard({ s }) {
  const c = indicatorStyle(s.indicator);
  return (
    <div style={{ background: 'white', border: '0.5px solid #e0e0e0', borderRadius: 12, padding: '1rem 1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: c.dot, flexShrink: 0 }}></div>
          <span style={{ fontWeight: 500, fontSize: 14 }}>{s.name}</span>
        </div>
        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, background: c.badge, color: c.badgeText, fontWeight: 500, whiteSpace: 'nowrap' }}>{c.label}</span>
      </div>
      <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>{s.description}</div>
      {s.incidents && s.incidents.length > 0 && (
        <div style={{ borderTop: '0.5px solid #e0e0e0', paddingTop: 8, marginTop: 8 }}>
          {s.incidents.map((inc, i) => (
            <div key={i} style={{ fontSize: 11, color: '#555', padding: '4px 0', borderBottom: i < s.incidents.length - 1 ? '0.5px solid #f0f0f0' : 'none', lineHeight: 1.4 }}>{inc}</div>
          ))}
        </div>
      )}
      <a href={s.url} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: '#2196C4', textDecoration: 'none', display: 'inline-block', marginTop: 8 }}>View status page →</a>
    </div>
  );
}

// ── Login Screen ──────────────────────────────────────────────────────────────
function LoginScreen({ onLogin, error }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f1f5f9' }}>
      <div style={{ background: 'white', borderRadius: 16, padding: '2.5rem', textAlign: 'center', maxWidth: 360, width: '100%', boxShadow: '0 4px 24px #0000001a' }}>
        <div style={{ width: 56, height: 56, background: '#2196C4', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.25rem' }}>
          <span style={{ fontSize: 28 }}>🐙</span>
        </div>
        <div style={{ fontWeight: 700, fontSize: 18, color: '#0D1B2E', marginBottom: 6 }}>IT Ops Status Dashboard</div>
        <div style={{ fontSize: 13, color: '#666', marginBottom: 24 }}>Octopus Deploy · Sign in with your company account to continue</div>
        {error && (
          <div style={{ background: '#FCEBEB', color: '#A32D2D', borderRadius: 8, padding: '10px 14px', fontSize: 12, marginBottom: 16 }}>{error}</div>
        )}
        <button onClick={onLogin} style={{ background: '#0D1B2E', color: 'white', border: 'none', borderRadius: 8, padding: '10px 24px', fontSize: 14, fontWeight: 600, cursor: 'pointer', width: '100%' }}>
          Sign in with Microsoft
        </button>
      </div>
    </div>
  );
}

// ── Access Denied Screen ──────────────────────────────────────────────────────
function AccessDenied({ user, onLogout }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f1f5f9' }}>
      <div style={{ background: 'white', borderRadius: 16, padding: '2.5rem', textAlign: 'center', maxWidth: 360, width: '100%', boxShadow: '0 4px 24px #0000001a' }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>🚫</div>
        <div style={{ fontWeight: 700, fontSize: 18, color: '#0D1B2E', marginBottom: 6 }}>Access Denied</div>
        <div style={{ fontSize: 13, color: '#666', marginBottom: 24 }}>
          <strong>{user}</strong> is not in the IT Ops group.<br />Contact your IT administrator to request access.
        </div>
        <button onClick={onLogout} style={{ background: 'transparent', color: '#666', border: '0.5px solid #ccc', borderRadius: 8, padding: '8px 20px', fontSize: 13, cursor: 'pointer' }}>
          Sign out
        </button>
      </div>
    </div>
  );
}

// ── Main App ──────────────────────────────────────────────────────────────────
export default function App() {
  const [authState, setAuthState] = useState('loading'); // loading | login | denied | authed
  const [user, setUser]           = useState(null);
  const [loginError, setLoginError] = useState(null);
  const [data, setData]           = useState(null);
  const [error, setError]         = useState(null);
  const [loading, setLoading]     = useState(true);
  const [lastFetched, setLastFetched] = useState(null);

  // ── Handle redirect after Microsoft login ──────────────────────────────────
  useEffect(() => {
    msalInstance.initialize().then(() => {
      msalInstance.handleRedirectPromise().then(response => {
        const account = response?.account || msalInstance.getAllAccounts()[0];
        if (account) {
          checkGroupAccess(account);
        } else {
          setAuthState('login');
        }
      }).catch(() => setAuthState('login'));
    });
  }, []);

  // ── Check if user is in the IT Ops group ──────────────────────────────────
  async function checkGroupAccess(account) {
    try {
      const tokenResponse = await msalInstance.acquireTokenSilent({
        scopes: ['User.Read', 'GroupMember.Read.All'],
        account,
      });
      const res = await fetch('https://graph.microsoft.com/v1.0/me/checkMemberGroups', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${tokenResponse.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ groupIds: [GROUP_ID] }),
      });
      const json = await res.json();
      const inGroup = json.value?.includes(GROUP_ID);
      setUser(account.username);
      setAuthState(inGroup ? 'authed' : 'denied');
    } catch (e) {
      if (e instanceof InteractionRequiredAuthError) {
        msalInstance.acquireTokenRedirect({ scopes: ['User.Read', 'GroupMember.Read.All'], account });
      } else {
        setAuthState('login');
      }
    }
  }

  // ── Sign in ────────────────────────────────────────────────────────────────
  async function handleLogin() {
    setLoginError(null);
    try {
      await msalInstance.loginRedirect({ scopes: ['User.Read', 'GroupMember.Read.All'] });
    } catch (e) {
      setLoginError('Sign in failed. Please try again.');
    }
  }

  // ── Sign out ───────────────────────────────────────────────────────────────
  function handleLogout() {
    msalInstance.logoutRedirect({ postLogoutRedirectUri: REDIRECT });
  }

  // ── Inactivity timeout — log out after 15 minutes of no action ───────────
  useEffect(() => {
    if (authState !== 'authed') return;
    let timer;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        msalInstance.logoutRedirect({ postLogoutRedirectUri: REDIRECT });
      }, 15 * 60 * 1000); // 15 minutes
    };
    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, reset));
    reset();
    return () => {
      clearTimeout(timer);
      events.forEach(e => window.removeEventListener(e, reset));
    };
  }, [authState]);

  // ── Fetch status data ──────────────────────────────────────────────────────
  async function loadStatus() {
    try {
      const res = await fetch(CF_URL + '?t=' + Date.now());
      const json = await res.json();
      setData(json);
      setLastFetched(new Date());
      setError(null);
    } catch (e) {
      setError('Failed to load status data.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (authState === 'authed') {
      loadStatus();
      const interval = setInterval(loadStatus, 60000);
      return () => clearInterval(interval);
    }
  }, [authState]);

  // ── Render ─────────────────────────────────────────────────────────────────
  if (authState === 'loading') return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>Loading...</div>
  );
  if (authState === 'login')  return <LoginScreen onLogin={handleLogin} error={loginError} />;
  if (authState === 'denied') return <AccessDenied user={user} onLogout={handleLogout} />;

  const services = data ? Object.values(data.services) : [];
  const degraded = services.filter(s => s.indicator !== 'none').length;

  return (
    <div style={{ fontFamily: 'sans-serif', maxWidth: 960, margin: '0 auto', padding: '2rem 1rem' }}>

      {/* Header */}
      <div style={{ background: '#0D1B2E', borderRadius: 12, padding: '1.25rem 1.5rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 36, height: 36, background: '#2196C4', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ color: 'white', fontSize: 18 }}>🐙</span>
          </div>
          <div>
            <div style={{ color: 'white', fontWeight: 500, fontSize: 16 }}>Octopus Deploy — IT Ops Status</div>
            <div style={{ color: '#7BA7C4', fontSize: 12, marginTop: 2 }}>Live service health monitoring</div>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ color: '#7BA7C4', fontSize: 11 }}>{user}</div>
          <div style={{ color: '#7BA7C4', fontSize: 11, marginTop: 2 }}>{lastFetched ? 'Updated: ' + formatTime(lastFetched.toISOString()) : 'Loading...'}</div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 6 }}>
            <button onClick={loadStatus} style={{ background: 'transparent', border: '0.5px solid #7BA7C4', color: '#7BA7C4', padding: '4px 12px', borderRadius: 6, fontSize: 11, cursor: 'pointer' }}>Refresh</button>
            <button onClick={handleLogout} style={{ background: 'transparent', border: '0.5px solid #7BA7C4', color: '#7BA7C4', padding: '4px 12px', borderRadius: 6, fontSize: 11, cursor: 'pointer' }}>Sign out</button>
          </div>
        </div>
      </div>

      {/* Overall health bar */}
      {!loading && data && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#f8f9fa', border: '0.5px solid #e0e0e0', borderRadius: 12, padding: '1rem 1.25rem', marginBottom: '1.5rem' }}>
          <div style={{ width: 12, height: 12, borderRadius: '50%', background: degraded === 0 ? '#639922' : '#BA7517', flexShrink: 0 }}></div>
          <div style={{ fontWeight: 500, fontSize: 14 }}>{degraded === 0 ? 'All systems operational' : `${degraded} service${degraded > 1 ? 's' : ''} with issues`}</div>
          <div style={{ marginLeft: 'auto', fontSize: 12, color: '#666' }}>{services.length - degraded} of {services.length} services operational</div>
        </div>
      )}

      {/* Loading and error states */}
      {loading && <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>Loading service statuses...</div>}
      {error && <div style={{ textAlign: 'center', padding: '2rem', color: '#A32D2D' }}>{error}</div>}

      {/* Service groups */}
      {data && Object.entries(GROUPS).map(([groupName, keys]) => (
        <div key={groupName} style={{ marginBottom: '2rem' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#0D1B2E', marginBottom: 12, paddingBottom: 6, borderBottom: '2px solid #0D1B2E', display: 'flex', alignItems: 'center', gap: 8 }}>
            {GROUP_ICONS[groupName]}
            {groupName}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
            {keys.map(k => data.services[k] && <ServiceCard key={k} s={data.services[k]} />)}
          </div>
        </div>
      ))}
    </div>
  );
}
